import {
    GetMeInvoicesSortOptionType,
    InvoiceKindType,
    InvoiceStatusType,
    SortDirectionType,
    type GetMeInvoicesQuery,
} from "@models/invoice";

// Billing History (#215) keeps its sort, filters and page in the URL so reload
// and Back work (the URL-as-source-of-truth convention, see useSearchFilters).
// The browser keys are readable slugs, deliberately not the API's int enums:
// `?sort=total&kind=order&status=voided&page=2`. Multi-value dimensions use a
// singular key repeated.

const SORT_KEY = "sort";
const KIND_KEY = "kind";
const STATUS_KEY = "status";
const PAGE_KEY = "page";

// The bare slugs sort descending (newest / highest first, the API's default
// direction); the `-asc` slugs are their ascending counterparts.
export type BillingHistorySort =
    | "issued-date"
    | "issued-date-asc"
    | "total"
    | "total-asc"
    | "invoice-number"
    | "invoice-number-asc";

// The mockup's default ("Issued Date"). Left out of the URL when chosen.
export const DEFAULT_BILLING_HISTORY_SORT: BillingHistorySort = "issued-date";

const SORTS: Record<
    BillingHistorySort,
    { sortBy: GetMeInvoicesSortOptionType; sortDirection: SortDirectionType }
> = {
    "issued-date": {
        sortBy: GetMeInvoicesSortOptionType.IssuedAt,
        sortDirection: SortDirectionType.Desc,
    },
    "issued-date-asc": {
        sortBy: GetMeInvoicesSortOptionType.IssuedAt,
        sortDirection: SortDirectionType.Asc,
    },
    total: {
        sortBy: GetMeInvoicesSortOptionType.TotalAmount,
        sortDirection: SortDirectionType.Desc,
    },
    "total-asc": {
        sortBy: GetMeInvoicesSortOptionType.TotalAmount,
        sortDirection: SortDirectionType.Asc,
    },
    "invoice-number": {
        sortBy: GetMeInvoicesSortOptionType.InvoiceNumber,
        sortDirection: SortDirectionType.Desc,
    },
    "invoice-number-asc": {
        sortBy: GetMeInvoicesSortOptionType.InvoiceNumber,
        sortDirection: SortDirectionType.Asc,
    },
};

// The dropdown, in display order: each ascending option beside its
// descending one.
export const BILLING_HISTORY_SORT_OPTIONS: {
    value: BillingHistorySort;
    label: string;
}[] = [
    { value: "issued-date", label: "Issued Date" },
    { value: "issued-date-asc", label: "Oldest first" },
    { value: "total", label: "Total" },
    { value: "total-asc", label: "Lowest total" },
    { value: "invoice-number", label: "Invoice Number" },
    { value: "invoice-number-asc", label: "Invoice number ascending" },
];

export const BILLING_HISTORY_KIND_OPTIONS: {
    slug: string;
    label: string;
    kind: InvoiceKindType;
}[] = [
    { slug: "order", label: "Orders", kind: InvoiceKindType.Order },
    {
        slug: "membership",
        label: "Memberships",
        kind: InvoiceKindType.Membership,
    },
];

export const BILLING_HISTORY_STATUS_OPTIONS: {
    slug: string;
    label: string;
    status: InvoiceStatusType;
}[] = [
    { slug: "issued", label: "Issued", status: InvoiceStatusType.Issued },
    { slug: "voided", label: "Voided", status: InvoiceStatusType.Voided },
];

// What the user chooses. The page is not a choice: any change here resets it.
export interface BillingHistoryChoices {
    sort: BillingHistorySort;
    kinds: InvoiceKindType[];
    statuses: InvoiceStatusType[];
}

export interface BillingHistoryParams extends BillingHistoryChoices {
    page: number;
}

// Unknown slugs are dropped and repeats collapse, so a hand-edited URL can
// neither send a value the API does not know nor count one filter twice.
// Option order, not URL order, so the same filters always build the same query.
const parseRepeated = <T>(
    params: URLSearchParams,
    key: string,
    options: { slug: string; value: T }[],
): T[] => {
    const slugs = params.getAll(key);
    return options
        .filter((option) => slugs.includes(option.slug))
        .map((option) => option.value);
};

export const parseBillingHistoryParams = (
    params: URLSearchParams,
): BillingHistoryParams => {
    const rawSort = params.get(SORT_KEY);
    const sort =
        BILLING_HISTORY_SORT_OPTIONS.find((option) => option.value === rawSort)
            ?.value ?? DEFAULT_BILLING_HISTORY_SORT;
    const page = Number(params.get(PAGE_KEY));

    return {
        sort,
        kinds: parseRepeated(
            params,
            KIND_KEY,
            BILLING_HISTORY_KIND_OPTIONS.map(({ slug, kind }) => ({
                slug,
                value: kind,
            })),
        ),
        statuses: parseRepeated(
            params,
            STATUS_KEY,
            BILLING_HISTORY_STATUS_OPTIONS.map(({ slug, status }) => ({
                slug,
                value: status,
            })),
        ),
        page: Number.isInteger(page) && page > 1 ? page : 1,
    };
};

// Writes the choices over the current params, keeping any other key, and
// drops the page: a narrowed list must not land on a page it no longer has.
export const writeBillingHistoryChoices = (
    current: URLSearchParams,
    next: BillingHistoryChoices,
): URLSearchParams => {
    const result = new URLSearchParams(current);
    result.delete(SORT_KEY);
    result.delete(KIND_KEY);
    result.delete(STATUS_KEY);
    result.delete(PAGE_KEY);

    if (next.sort !== DEFAULT_BILLING_HISTORY_SORT) {
        result.set(SORT_KEY, next.sort);
    }
    BILLING_HISTORY_KIND_OPTIONS.filter((option) =>
        next.kinds.includes(option.kind),
    ).forEach((option) => result.append(KIND_KEY, option.slug));
    BILLING_HISTORY_STATUS_OPTIONS.filter((option) =>
        next.statuses.includes(option.status),
    ).forEach((option) => result.append(STATUS_KEY, option.slug));

    return result;
};

export const writeBillingHistoryPage = (
    current: URLSearchParams,
    page: number,
): URLSearchParams => {
    const result = new URLSearchParams(current);
    if (page <= 1) {
        result.delete(PAGE_KEY);
    } else {
        result.set(PAGE_KEY, String(page));
    }

    return result;
};

export const toMeInvoicesQuery = (
    params: BillingHistoryParams,
    pageSize: number,
): GetMeInvoicesQuery => ({
    kinds: params.kinds,
    statuses: params.statuses,
    ...SORTS[params.sort],
    pageNumber: params.page,
    pageSize,
});
